import { useCallback } from 'react';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import {
	isValidationError,
	isClientFetchError,
	isHttpClientErrorResponse,
} from '@atlassian/jira-fetch';
import { useFlagsService } from '@atlassian/jira-flags';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { CARD_ACTION_DELETE_EXPERIENCE } from '../../common/constants';
import type { BoardIssueOld } from '../../common/types';
import { useBoardStore } from '../../services/board-store';
import { useDeleteIssue } from '../../services/delete-issue';
import messages from './messages';

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useDeleteIssueAction = () => {
	const deleteIssue = useDeleteIssue();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();
	const [, { pushToJobQueue, removeIssue }] = useBoardStore();

	const startExperience = useExperienceStart(CARD_ACTION_DELETE_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(CARD_ACTION_DELETE_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(CARD_ACTION_DELETE_EXPERIENCE);

	return useCallback(
		(issue: BoardIssueOld) => {
			const { id, key } = issue;
			startExperience();
			pushToJobQueue(() =>
				deleteIssue({ issueKey: key })
					.then(() => {
						removeIssue(id);
						showFlag({
							type: 'success',
							title: messages.deleteSuccessTitle,
							description: messages.deleteSuccessDescription,
						});
						markExperienceSuccess();
						fireTrackAnalytics(createAnalyticsEvent({ actionSubjectId: id }), 'issue deleted', {
							throughActionCard: true,
							issueId: issue.id,
						});
					})
					.catch((err) => {
						showFlag({
							type: 'error',
							title: messages.deleteErrorTitle,
							description: messages.deleteErrorDescription,
						});
						const reportedError =
							err &&
							(isClientFetchError(err) || isValidationError(err) || isHttpClientErrorResponse(err));
						// Exclude client fetch, validation and 4xx errors from experience fail events reporting
						if (!reportedError) {
							markExperienceFailed('delete issue failed', err);
						} else {
							markExperienceSuccess();
						}
					}),
			);
		},
		[
			startExperience,
			pushToJobQueue,
			deleteIssue,
			removeIssue,
			showFlag,
			markExperienceSuccess,
			createAnalyticsEvent,
			markExperienceFailed,
		],
	);
};
